import { useDrag } from 'react-dnd'
import { useNavigate, useParams } from 'react-router-dom'

export interface Vendor {
  id: string  // vendor ID (for display/navigation)
  submissionId: string  // submission ID (for API updates)
  name: string
  productName?: string  // product name (shown first on card if available)
  website: string
  status: string
  statusDetail: string
  technical: number
  manufacturing: number
  vcScorecard: number
  dodReadiness: number
}

interface VendorCardProps {
  vendor: Vendor
  columnId: string
}

const DotRating = ({ rating }: { rating: number }) => {
  const dots = []
  for (let i = 0; i < 5; i++) {
    const fillPercentage = Math.min(Math.max(rating - i, 0), 1)
    
    if (fillPercentage === 0) {
      // Empty dot
      dots.push(
        <div key={i} className="w-[6px] h-[6px] rounded-full bg-slate-700 dark:bg-slate-700" />
      )
    } else if (fillPercentage === 1) {
      // Full dot
      dots.push(
        <div key={i} className="w-[6px] h-[6px] rounded-full bg-green-500 dark:bg-green-500" />
      )
    } else {
      // Partial dot
      dots.push(
        <div key={i} className="relative w-[6px] h-[6px]">
          <div className="w-full h-full rounded-full bg-slate-700 dark:bg-slate-700" />
          <div 
            className="absolute top-0 left-0 h-full rounded-full bg-green-500 dark:bg-green-500 overflow-hidden"
            style={{ width: `${fillPercentage * 100}%` }}
          />
        </div>
      )
    }
  }
  return <div className="flex gap-[3px] items-center shrink-0">{dots}</div>
}

export function VendorCard({ vendor, columnId }: VendorCardProps) {
  const navigate = useNavigate()
  const { programId } = useParams<{ programId: string }>()
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'VENDOR',
    item: { vendor, sourceColumn: columnId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  const handleClick = () => {
    // Only navigate if not actively dragging
    if (!isDragging) {
      navigate(`/programs/${programId}/vendors/${vendor.id}`)
    }
  }

  return (
    <div
      ref={drag}
      onClick={handleClick}
      className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 cursor-pointer hover:border-blue-500 dark:hover:border-blue-500 transition-all hover:shadow-md ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="bg-slate-200 dark:bg-slate-700 rounded size-8 flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900 dark:text-slate-50 truncate">
            {vendor.productName || vendor.name}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 truncate mt-0.5">
            {vendor.productName ? vendor.name : vendor.website}
          </p>
        </div>
      </div>
      
      {/* Ratings Section */}
      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            Technical
          </p>
          <DotRating rating={vendor.technical} />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            Prod. Readiness
          </p>
          <DotRating rating={vendor.manufacturing} />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            Stability
          </p>
          <DotRating rating={vendor.vcScorecard} />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            DoW Partnership
          </p>
          <DotRating rating={vendor.dodReadiness} />
        </div>
      </div>
      
      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
        <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">
          {vendor.status}
        </p>
        <p className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">
          {vendor.statusDetail}
        </p>
      </div>
    </div>
  )
}

